class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.secondaryPanel = this.querySelector("#CartDrawer-SecondaryPanel");
    this.primaryPanel = this.querySelector(".drawer__primary-panel");

    this.addEventListener("keyup", (evt) => {
      if (evt.code === "Escape") {
        if (
          this.secondaryPanel &&
          this.secondaryPanel.classList.contains("active")
        ) {
          this.closeSecondaryPanel();
        } else {
          this.close();
        }
      }
    });

    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );

    this.setHeaderCartIconAccessibility();
    this.setupSecondaryPanelTriggers();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setupSecondaryPanelTriggers() {
    // Open triggers
    this.querySelectorAll("[data-secondary-panel-open]").forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.openSecondaryPanel();
      });

      // Keyboard support
      trigger.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.openSecondaryPanel();
        }
      });
    });

    // Close triggers
    this.querySelectorAll("[data-secondary-panel-close]").forEach(
      (trigger) => {
        trigger.addEventListener("click", (e) => {
          e.preventDefault();
          this.closeSecondaryPanel();
        });
      }
    );
  }

  openSecondaryPanel() {
    if (!this.secondaryPanel) return;

    this.secondaryPanel.classList.add("active");
    this.primaryPanel?.classList.add("secondary-active");
    this.classList.add("secondary-panel-open");

    // Focus into secondary panel
    setTimeout(() => {
      const focusElement =
        this.secondaryPanel.querySelector(".secondary-panel__back") ||
        this.secondaryPanel.querySelector(".secondary-panel__close");
      if (focusElement) focusElement.focus();
    }, 300);
  }

  closeSecondaryPanel() {
    if (!this.secondaryPanel) return;

    this.secondaryPanel.classList.remove("active");
    this.primaryPanel?.classList.remove("secondary-active");
    this.classList.remove("secondary-panel-open");

    const trigger = this.querySelector("[data-secondary-panel-open]");
    if (trigger) trigger.focus();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    // Close secondary first if open
    if (
      this.secondaryPanel &&
      this.secondaryPanel.classList.contains("active")
    ) {
      this.closeSecondaryPanel();
    }

    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      // Re-setup secondary triggers after re-render
      this.setupSecondaryPanelTriggers();
      this.secondaryPanel = this.querySelector("#CartDrawer-SecondaryPanel");
      this.primaryPanel = this.querySelector(".drawer__primary-panel");
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
